// ─── UTILITY FUNCTIONS ────────────────────────────────────────────────────────

export const uid = () => Math.random().toString(36).slice(2, 10);

// ── safeLocalStorage ──────────────────────────────────────────────────────────
//
//  GDPR-aware localStorage wrapper.  Preference keys (feed tab, editor tour,
//  font size…) only persist to localStorage when preferences consent is given.
//  If consent is not granted the value lives in a per-session in-memory Map.
//  Necessary keys (auth, theme) are always persisted regardless of consent.
//
//  Usage:
//    import { safeLocalStorage } from './utils';
//    const ls = safeLocalStorage(consent);
//    ls.set('feedTab', 'trending');  // persisted only if consent.preferences
//    ls.get('feedTab', 'recent');    // returns stored value or default
//
const PREFERENCE_KEYS = new Set([
  'feedTab', 'editorTour', 'fontSize', 'feedCategory', 'feedFilter',
]);
const _memoryStore = new Map();

export const safeLocalStorage = (consent = {}) => ({
  get: (key, defaultVal = null) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw !== null) {
        try { return JSON.parse(raw); } catch { return raw; }
      }
      return _memoryStore.has(key) ? _memoryStore.get(key) : defaultVal;
    } catch { return defaultVal; }
  },
  set: (key, value) => {
    const isPreference = PREFERENCE_KEYS.has(key);
    if (!isPreference || consent.preferences) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
    } else {
      _memoryStore.set(key, value);
    }
  },
  remove: (key) => {
    try { localStorage.removeItem(key); } catch {}
    _memoryStore.delete(key);
  },
});

// ── Date key format ───────────────────────────────────────────────────────────
//
//  Dates are stored as 12-char sortable strings: "YYYYYY-MM-DD"
//  The year field is the astronomical year biased by +99999 so that:
//    BC 9999 → astro -9998 → biased 00001
//    BC    1 → astro     0 → biased 99999
//    AD    1 → astro     1 → biased 100000
//    AD 9999 → astro  9999 → biased 109998
//  Plain string comparison then gives correct chronological order.
//
//  Legacy dates (YYYY-MM-DD, 10 chars) are also handled gracefully.

export const toDateKey = (year, month, day, era) => {
  // era: 'AD' | 'BC'.  year is always a positive display integer (>= 1).
  const astro  = era === 'BC' ? 1 - year : year;
  const biased = astro + 99999;
  const yy = biased.toString().padStart(6, '0');
  const mm = month.toString().padStart(2, '0');
  const dd = day.toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

export const fromDateKey = (key) => {
  // New 12-char format
  const biased = parseInt(key.slice(0, 6), 10);
  const month  = parseInt(key.slice(7, 9), 10);
  const day    = parseInt(key.slice(10, 12), 10);
  const astro  = biased - 99999;
  const era    = astro >= 1 ? 'AD' : 'BC';
  const year   = astro >= 1 ? astro : 1 - astro;
  return { year, month, day, era };
};

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const formatDate = (dateStr, showEra = true) => {
  if (!dateStr) return '';

  if (dateStr.length === 12) {
    // New format
    const { year, month, day, era } = fromDateKey(dateStr);
    const suffix = showEra && era === 'BC' ? ' B.C.' : '';
    // Only include the parts that were explicitly entered (0 means "not entered")
    if (month === 0) return `${year}${suffix}`;
    if (day === 0)   return `${MONTH_NAMES[month - 1]} ${year}${suffix}`;
    return `${MONTH_NAMES[month - 1]} ${day}, ${year}${suffix}`;
  }

  // Legacy YYYY-MM-DD
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const daysBetween = (a, b) => {
  // Approximate days — good enough for timeline positioning
  const toDays = (key) => {
    if (key.length === 12) {
      const { year, month, day, era } = fromDateKey(key);
      const astro = era === 'BC' ? 1 - year : year;
      return astro * 365.25 + month * 30.44 + day;
    }
    // Legacy
    return new Date(key + 'T00:00:00').getTime() / 86400000;
  };
  return Math.max(1, Math.round(toDays(b) - toDays(a)));
};

export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// Strip HTML tags and return plain text content
export const stripHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

// Like stripHtml but preserves leading/trailing whitespace.
// Use this for live-editing inputs where trimming would lose the user's
// trailing space mid-typing.
export const stripHtmlKeepSpaces = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

// Removes <img> tags from an HTML string but keeps the rest of the markup.
// Used to render description previews without the embedded images
// (the images are surfaced separately as a banner via getTimelinePreviewImage).
export const stripImgTags = (html) => {
  if (!html) return '';
  return html.replace(/<img\b[^>]*\/?>/gi, '');
};

// Returns true if HTML string contains any visible text content
export const hasTextContent = (html) => stripHtml(html).length > 0;

// Compact engagement count formatter.
//   < 1000        → exact integer ("0", "42", "999")
//   1000 to 9999  → one decimal place ("1.2k", "9.9k")
//   10000+        → no decimal ("10k", "23k", "999k")
//   1_000_000+    → one decimal millions ("1.2m", "23.4m")
// Used for view / like / comment / bookmark counts on preview cards.
export const formatCompactCount = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return '0';
  if (v < 1000)        return String(Math.floor(v));
  if (v < 10_000)      return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  if (v < 1_000_000)   return Math.floor(v / 1000) + 'k';
  return (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm';
};

// Human-readable relative time ("3h ago", "2d ago", etc.)
export const timeAgo = (ts) => {
  if (!ts) return '';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60)  return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  if (d < 30)  return `${Math.floor(d / 7)}w ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

// Returns true if HTML contains visible text OR an image — used to guard
// description render so an image-only description still displays
export const hasVisibleContent = (html) => {
  if (!html) return false;
  const div = document.createElement('div');
  div.innerHTML = html;
  const hasText = (div.textContent || div.innerText || '').trim().length > 0;
  const hasImg  = !!div.querySelector('img');
  return hasText || hasImg;
};

/** Returns the first image embedded in the timeline description, if any. */
// Returns '#000000' or '#ffffff' — whichever has better contrast against the given hex background.
export const getContrastColor = (hex) => {
  if (!hex || typeof hex !== 'string') return '#000000';
  const c = hex.replace('#', '').slice(0, 6);
  if (c.length < 6) return '#000000';
  const toLinear = v => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * toLinear(parseInt(c.slice(0,2),16))
          + 0.7152 * toLinear(parseInt(c.slice(2,4),16))
          + 0.0722 * toLinear(parseInt(c.slice(4,6),16));
  return L > 0.179 ? '#000000' : '#ffffff';
};

export const getTimelinePreviewImage = (tl) => {
  if (!tl.description) return null;
  const m = tl.description.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m ? m[1] : null;
};
